import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd, waitForAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    // CYGNUSS 22/10/2026: a gaveta pode ja estar aberta quando este componente chega (toque cedo no cabecalho,
    // ver header-actions.liquid, que marca o dialog com data-cyg-aberto-cedo). So essa gaveta marcada e assumida:
    // fecha pelo fundo e pelo Esc e devolve a rolagem certa. Qualquer outro dialog segue igual a antes.
    const { dialog } = this.refs;
    if (dialog?.hasAttribute('data-cyg-aberto-cedo')) {
      dialog.removeAttribute('data-cyg-aberto-cedo');
      if (dialog.open) this.#adoptOpenDialog();
    }
  }

  /**
   * CYGNUSS 30/10/2026: rotina unica da gaveta do carrinho (window.cygGaveta, no script de toque cedo de
   * header-actions.liquid): abre como popover quando pode, trava, guarda o toque fora, prende o foco e fecha. So a
   * cart-drawer-component devolve a rotina (cart-drawer.js); os outros dialogs seguem com showModal.
   * @returns {any}
   */
  get gavetaLeve() {
    return null;
  }

  /**
   * Takes over a dialog that was opened before this component was defined.
   */
  #adoptOpenDialog() {
    const { dialog } = this.refs;
    const gaveta = this.gavetaLeve;

    if (gaveta?.emPopover(dialog)) {
      // CYGNUSS 30/10/2026: aberta cedo como popover: a guarda, o Esc, a trava e o foco ja estao armados pela rotina
      // unica; daqui em diante o toque fora e o Esc fecham pelo closeDialog, com a animacao.
      gaveta.adotar(dialog, { aoFechar: this.closeDialog });
    } else {
      const { style } = document.body;
      const top = parseFloat(style.top);

      if (style.position === 'fixed' && !Number.isNaN(top)) this.#previousScrollY = -top;

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
      if (gaveta) dialog.addEventListener('close', this.#fechouSozinha);
    }

    // CYGNUSS 22/10/2026: avisa "gaveta aberta" como o showDialog faz, para quem ouve esse aviso (resumo fixo do
    // carrinho, brindes, embrulho, contagem, carrossel de sugestoes) agir na gaveta que abriu cedo. Espera os outros
    // scripts da pagina se registrarem antes de avisar.
    const avisarAberta = () => {
      if (dialog.open) this.dispatchEvent(new DialogOpenEvent());
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', avisarAberta, { once: true });
    } else {
      queueMicrotask(avisarAberta);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  #fechando = false;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // CYGNUSS 30/10/2026: gaveta do carrinho como popover (etapa 8, item 3). showModal deixava a pagina inteira inerte
    // e o navegador recalculava o estilo de tudo ao abrir e ao fechar (355-608 ms num quadro com a CPU lenta). A rotina
    // unica decide o caminho (popover nativo, sem outro dialog modal aberto, sem html[data-cyg-gaveta="antigo"]), abre
    // no proximo quadro e so depois da primeira pintura foca o X e avisa "gaveta aberta". Ela tambem barra o 2o
    // pedido de abrir antes de abrir (o "Adicionar" pedia duas vezes).
    const gaveta = this.gavetaLeve;
    if (gaveta) {
      if (gaveta.abrindo(dialog)) return;
      if (gaveta.usaPopover()) {
        gaveta.abrir(dialog, {
          aoFechar: this.closeDialog,
          reserva: () => this.#showModalDialog(),
          depois: () => this.dispatchEvent(new DialogOpenEvent()),
        });
        return;
      }
    }

    this.#showModalDialog();
  }

  /**
   * Shows the dialog as a modal, with the body fixed (the path from before, and the only one for the other dialogs).
   */
  #showModalDialog() {
    const { dialog } = this.refs;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
      if (this.gavetaLeve) dialog.addEventListener('close', this.#fechouSozinha);
    });
  }

  /**
   * CYGNUSS 30/10/2026: gaveta do carrinho no caminho de antes (showModal) fechada pelo proprio navegador, sem o
   * closeDialog: gesto ou botao Voltar do Android, ou Esc com o foco no body. Antes o body ficava fixo e a pagina
   * parava de rolar. Agora a rotina unica solta o body e volta a rolagem, e sai o aviso "gaveta fechada", como no
   * closeDialog. O closeDialog tira este ouvinte antes de fechar, entao nada roda duas vezes.
   */
  #fechouSozinha = () => {
    const { dialog } = this.refs;

    dialog.removeEventListener('close', this.#fechouSozinha);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    this.gavetaLeve?.fechar(dialog, this.#previousScrollY);
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    const gaveta = this.gavetaLeve;

    if (gaveta?.emPopover(dialog)) {
      await this.#closePopoverDialog(gaveta);
      return;
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    dialog.removeEventListener('close', this.#fechouSozinha);

    // CYGNUSS 13/10/2026: reinicia a animacao sem forcar layout no meio do clique (o "void offsetWidth" custava um
    // recalculo de estilo da pagina inteira, ~1-1,5 s com CPU lenta): zera a animacao, espera um quadro e so entao fecha
    dialog.style.animation = 'none';
    await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    if (gaveta) {
      // CYGNUSS 30/10/2026: gaveta do carrinho no caminho de antes (showModal): a mesma rotina unica fecha, solta o body
      // fixo e volta a rolagem.
      gaveta.fechar(dialog, this.#previousScrollY);
    } else {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

      dialog.close();
    }
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * CYGNUSS 30/10/2026: fecha a gaveta do carrinho aberta como popover. Mesma animacao de saida de antes, esperada
   * pelos eventos da propria gaveta (waitForAnimationEnd, sem getAnimations); depois a rotina unica tira a guarda,
   * fecha, destrava e devolve o foco. Toque fora ou Esc durante a saida nao comecam outra.
   * @param {any} gaveta - A rotina unica (window.cygGaveta).
   */
  async #closePopoverDialog(gaveta) {
    if (this.#fechando) return;
    this.#fechando = true;

    const { dialog } = this.refs;

    try {
      dialog.style.animation = 'none';
      await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));

      dialog.classList.add('dialog-closing');
      dialog.style.animation = '';

      await waitForAnimationEnd(dialog);

      gaveta.fechar(dialog);
      dialog.classList.remove('dialog-closing');
    } finally {
      this.#fechando = false;
    }

    this.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          // CYGNUSS 30/10/2026: o menu do celular marca o details no toque (data-cyg-trava-depois, header-drawer.js) e
          // trava sozinho, uma vez, logo depois da primeira pintura; aqui a trava chegava em hora incerta (o toggle as
          // vezes chega antes da primeira pintura da gaveta). Sem a marca
          // (outros details, toque cedo antes do componente, html[data-cyg-gaveta="antigo"]) trava aqui como antes. O
          // destravar segue aqui para todos: fechou por qualquer caminho, a pagina volta a rolar.
          if (event.target.hasAttribute('data-cyg-trava-depois')) return;
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
